import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Link2, Shield, SlidersHorizontal, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant, useTenants } from '@/store/tenant';
import { getIdpProvider } from '@/types';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InfoGrid, InfoItem } from '@/components/ui/InfoGrid';
import { InputField } from '@/components/ui/InputField';
import { TabBar } from '@/components/ui/TabBar';
import { EndpointsSection, OptionsSection, ScopesSection } from './components/idp-oauth-sections';
import {
  emptyOAuthConfig,
  toOAuthConfig,
  validateOAuthConfig,
  type DiscoveryResponse,
  type OAuthConfig,
} from './components/idp-oauth-config';

interface Connection {
  baseUrl: string;
  clientId: string;
  clientSecret: string;
  redirectUri: string;
}

export const ConfigureTenantIdpPage = () => {
  const { id, idpId } = useParams<{ id: string; idpId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setBreadcrumbs } = useLayout();
  const tenantStore = useTenant(id);
  const getTenant = useTenants((state) => state.get);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connection, setConnection] = useState<Connection>({ baseUrl: '', clientId: '', clientSecret: '', redirectUri: '' });
  const [oauth, setOauth] = useState<OAuthConfig>(emptyOAuthConfig());
  const [discovery, setDiscovery] = useState<DiscoveryResponse | null>(null);

  const back = `/m/tenants/${id}?tab=idps`;
  const idp = tenantStore.idps.available.find((i) => i.id === idpId);
  const existing = tenantStore.idps.data.find((c) => c.idp_id === idpId);
  const isEditing = !!existing;
  const provider = idp?.provider ?? '';
  const isEntra = provider === 'entra';
  const isCustom = provider === 'custom';
  const meta = getIdpProvider(provider);
  const logo = idp?.logo_url || meta?.logoUrl;

  useEffect(() => {
    if (!id || !idpId) return;
    let cancelled = false;
    (async () => {
      try {
        const [tenant] = await Promise.all([getTenant(id), tenantStore.idps.list(), tenantStore.idps.listAvailable()]);
        if (cancelled) return;
        const scoped = useTenants.getState().tenantScoped[id];
        const config = scoped?.idps.find((c) => c.idp_id === idpId);
        const full = scoped?.availableIdps.find((i) => i.id === idpId);
        if (config) {
          setConnection({
            baseUrl: config.base_url || '',
            clientId: config.client_id || config.client_id_keyvault_ref || '',
            clientSecret: '',
            redirectUri: config.redirect_uri || config.redirect_uri_keyvault_ref || '',
          });
          setOauth(toOAuthConfig(config.oauth_config));
        }
        setBreadcrumbs([
          { name: 'Tenants', to: '/m/tenants' },
          { name: tenant.name, to: back },
          { name: full?.display_name || 'Identity Provider' },
        ]);
      } catch (error) {
        console.error('Failed to load identity provider configuration:', error);
        toast.error('Failed to load identity provider configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, idpId]);

  const tabs = [
    { id: 'connection', label: 'Connection', icon: KeyRound },
    ...(isEntra
      ? []
      : [
          { id: 'endpoints', label: 'Endpoints', icon: Link2 },
          { id: 'scopes', label: 'Scopes & Options', icon: SlidersHorizontal },
        ]),
  ];
  const requested = searchParams.get('tab') ?? 'connection';
  const activeTab = tabs.some((t) => t.id === requested) ? requested : 'connection';
  const setTab = (tab: string) => setSearchParams({ tab }, { replace: true });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !idpId) return;

    if (!connection.baseUrl || !connection.clientId || !connection.redirectUri) {
      toast.error('Issuer URL, Client ID and Redirect URI are required');
      setTab('connection');
      return;
    }
    const oauthError = isEntra ? null : validateOAuthConfig(oauth, provider);
    if (oauthError) {
      toast.error(oauthError);
      setTab('endpoints');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        base_url: connection.baseUrl,
        client_id: connection.clientId,
        client_secret: connection.clientSecret || undefined,
        redirect_uri: connection.redirectUri,
        // Entra signs in through the platform's app registration, so there is nothing to store
        oauth_config: isEntra ? undefined : oauth,
      };
      if (isEditing) await tenantStore.idps.update(idpId, payload);
      else await tenantStore.idps.assign(idpId, payload);
      toast.success('Identity provider configured successfully');
      navigate(back);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to configure identity provider');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!idp) {
    return (
      <div className="p-8 text-center text-slate-500">
        This identity provider is not available.{' '}
        <button className="text-(--primary) font-semibold" onClick={() => navigate(back)}>
          Back to tenant
        </button>
      </div>
    );
  }

  const set = (patch: Partial<Connection>) => setConnection({ ...connection, ...patch });

  return (
    <form
      onSubmit={save}
      // Enter in a text field must not save the whole configuration; only the Save button does
      onKeyDown={(e) => {
        const target = e.target as HTMLElement;
        if (e.key === 'Enter' && target.tagName === 'INPUT') e.preventDefault();
      }}
      className="p-4 lg:p-8 max-w-5xl mx-auto w-full animate-in fade-in duration-500"
    >
      <PageHeader
        title={idp.display_name}
        subtitle={isEditing ? 'Update how this tenant signs in with this provider.' : 'Set up this provider for the tenant.'}
        actions={
          <Button type="button" variant="ghost" onClick={() => navigate(back)} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        }
      />

      <div className="mt-8">
        {tabs.length > 1 && <TabBar tabs={tabs} activeTab={activeTab} onChange={setTab} className="mb-8" />}

        <div className="grid gap-6 animate-in slide-in-from-bottom-4 duration-500">
          {activeTab === 'connection' && (
            <>
              <Card title="Provider" subtitle="Global provider settings cannot be changed at the tenant level." className="glass">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-xl border border-slate-200/60 dark:border-slate-700/60 shadow bg-white flex items-center justify-center overflow-hidden">
                    {logo ? <img src={logo} alt="" className="w-8 h-8 object-contain" /> : <Shield className="w-6 h-6 text-slate-400" />}
                  </div>
                  <InfoGrid cols={2} className="flex-1">
                    <InfoItem label="Identity provider" value={idp.display_name} />
                    <InfoItem label="Type" value={meta?.name ?? provider} />
                  </InfoGrid>
                </div>
              </Card>

              <Card title="Connection" subtitle="The application registered with the identity provider for this tenant." className="glass">
                <InfoGrid cols={2}>
                  <InputField
                    label="Issuer URL"
                    placeholder="e.g. https://your-domain.okta.com"
                    value={connection.baseUrl}
                    onChange={(e) => set({ baseUrl: e.target.value })}
                    helpText="The issuer URL of the identity provider."
                    className="md:col-span-2"
                  />
                  <InputField
                    label="Client ID"
                    placeholder="e.g. 0oa123456789"
                    value={connection.clientId}
                    onChange={(e) => set({ clientId: e.target.value })}
                    helpText="The application client ID from the identity provider."
                  />
                  <InputField
                    label="Client Secret"
                    type="password"
                    placeholder={existing?.client_secret_masked || 'Enter secret…'}
                    value={connection.clientSecret}
                    onChange={(e) => set({ clientSecret: e.target.value })}
                    helpText={isEditing ? 'Leave blank to keep the existing secret.' : 'The application client secret.'}
                  />
                  <InputField
                    label="Redirect URI"
                    placeholder="e.g. https://your-app.com/callback"
                    value={connection.redirectUri}
                    onChange={(e) => set({ redirectUri: e.target.value })}
                    helpText="The URI where the identity provider sends the response."
                    className="md:col-span-2"
                  />
                </InfoGrid>
              </Card>

              {isEntra && (
                <p className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800/40 rounded-lg px-3 py-2">
                  Microsoft Entra signs users in through the platform&apos;s app registration, so endpoints and scopes are not
                  configured per tenant.
                </p>
              )}
            </>
          )}

          {activeTab === 'endpoints' && (
            <EndpointsSection
              value={oauth}
              onChange={setOauth}
              isCustom={isCustom}
              discovery={discovery}
              onDiscovered={setDiscovery}
            />
          )}

          {activeTab === 'scopes' && (
            <>
              <ScopesSection value={oauth} onChange={setOauth} discovery={discovery} />
              <OptionsSection value={oauth} onChange={setOauth} />
            </>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 mt-8 -mx-4 lg:-mx-8 px-4 lg:px-8 py-4 bg-white/90 dark:bg-slate-950/90 backdrop-blur border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
        <Button type="button" variant="ghost" onClick={() => navigate(back)} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" isLoading={saving} className="gap-2">
          <Check className="w-4 h-4" />
          Save Configuration
        </Button>
      </div>
    </form>
  );
};
